import { useEffect, useRef, useState } from "react";

// Fired after a write so every other instance on the same key reloads: the
// settings sheet and the feature it configures are separate hook instances.
// Anything else that writes a preference key by hand announces the same way.
export const LOCAL_STORAGE_SYNC_EVENT = "qk:local-storage";

export function announceLocalStorageChange(key) {
  window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_SYNC_EVENT, { detail: key }));
}

/**
 * Like useState, but the value is persisted to localStorage under `key`
 * and restored on mount + every time `key` changes. Any JSON-serializable
 * value works — strings, arrays, objects.
 *
 *   const [draft, setDraft] = useLocalStorageState("chat:draft:42", "");
 *   const [items, setItems] = useLocalStorageState("toolkit:42", []);
 *
 * Pass `null`/`""` for `key` while the scope is unknown (e.g. waiting for
 * `bookId`); the hook becomes a no-op until a real key arrives. When the
 * value equals `defaultValue` the entry is removed from storage so dead
 * keys don't accumulate.
 *
 * Race-safe across key changes: a guard ref ensures the persist effect
 * never writes the previous key's value to the new key in the brief
 * window after `key` changes but before the load effect has run.
 */
export default function useLocalStorageState(
  key,
  defaultValue,
  { debounceMs = 300 } = {},
) {
  const [value, setValue] = useState(defaultValue);
  const loadedKeyRef = useRef(null);

  useEffect(() => {
    if (!key) {
      loadedKeyRef.current = null;
      setValue(defaultValue);
      return undefined;
    }
    const load = () => {
      try {
        const raw = localStorage.getItem(key);
        setValue(raw === null ? defaultValue : JSON.parse(raw));
      } catch {
        setValue(defaultValue);
      }
    };
    load();
    loadedKeyRef.current = key;
    const onSync = (event) => {
      if (event.detail === key) load();
    };
    window.addEventListener(LOCAL_STORAGE_SYNC_EVENT, onSync);
    return () => window.removeEventListener(LOCAL_STORAGE_SYNC_EVENT, onSync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!key || loadedKeyRef.current !== key) return undefined;
    const t = setTimeout(() => {
      try {
        const serialized = JSON.stringify(value);
        const isEmpty = serialized === JSON.stringify(defaultValue);
        const stored = localStorage.getItem(key);
        // Already in storage (including a value another instance just wrote):
        // no write, and no sync echo.
        if (isEmpty ? stored === null : stored === serialized) return;
        if (isEmpty) localStorage.removeItem(key);
        else localStorage.setItem(key, serialized);
        announceLocalStorageChange(key);
      } catch {
        // ignore — quota / private mode / etc.
      }
    }, debounceMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, value, debounceMs]);

  return [value, setValue];
}
